import {Component, Prop, Vue} from "vue-property-decorator";
import {HTTP} from "@/utils/httpCommonUtil";
import VueSimpleSpinner from "vue-simple-spinner";
import {ContactGroupModel} from "@/components/communications/models/contactModel";
import FilterComponent from "@/componentsReusable/FilterComponent.vue";
import ActionsDropdown from "@/componentsReusable/dropdowns/ActionsDropdown.vue";
import {
  CONTACT_GROUP_ACTIONS,
  ContactGroupAction,
} from "@/components/communications/cards/actions/Actions";
import GenericModal from "@/componentsReusable/genericModal.vue";
import * as communicationsFilterEnums from "@/enums/communicationsFilterEnums";
import {ViewStateType} from "@/enums/ViewStateType";

@Component({
  components: {
    VueSimpleSpinner,
    FilterComponent,
    ActionsDropdown,
    GenericModal,
  },
})
export default class CampaignGroupCard extends Vue {

  @Prop({type: Number, required: true, default: ''})
  recipientTypeId!: number;

  protected contactGroups: ContactGroupModel[] = [];
  protected selectedGroup = {} as ContactGroupModel;
  protected expandingContactId = null as number | null;
  protected showDeleteGroupConfirmationModal = false;
  protected showRenameGroupModal = false;
  protected showCopyGroupModal = false;
  protected groupCardExpanded = false;
  protected newGroupName = '';
  protected searchInput = '';
  protected loadingInitialData = false;
  protected loading = false;
  protected showActions = false;
  protected actions = CONTACT_GROUP_ACTIONS;
  protected actionTop = null as number | null;
  protected actionRight = 10;
  protected filtersSelected: any[] = [];
  protected defaultFilters: any[] = [];
  protected defaultLoggedInUser = {};
  protected performSearchTimeout = null as any;
  protected filterTypes = {
    [communicationsFilterEnums.TEAM_MEMBER_FILTER_TYPE]: {
      id: communicationsFilterEnums.TEAM_MEMBER_FILTER_TYPE,
      name: "Team Member",
      isSelected: true,
      searchAPI: '/api/users/search?userNameLike=',
      conditionResponseData: function (responseData: any) {
        return responseData.map((responseObject: any) => {
          responseObject.filterTypeId = communicationsFilterEnums.TEAM_MEMBER_FILTER_TYPE;
          return responseObject;
        });
      }
    },
  }

  async created() {
    await this.setDefaultLoggedInUser();
    this.setDefaultFilters();
    this.searchInput = this.$store.state.viewState[ViewStateType.COMMUNICATION_GROUP_SEARCH] ? this.$store.state.viewState[ViewStateType.COMMUNICATION_GROUP_SEARCH] : '';
    await this.getAllContactGroups();
  }

  expandCard() {
    this.groupCardExpanded = !this.groupCardExpanded;
  }

  async searchMessages() {
    this.performSearchTimeout = setTimeout( async () => {
      await this.getAllContactGroups();
    }, 100);
  }

  async filterChanged (filters: any[]) {
    this.performSearchTimeout = setTimeout( async () => {
      this.filtersSelected = filters;
      await this.getAllContactGroups();
    }, 100);
  }

  async getAllContactGroups() {
    try {
      let currentSearchInput = this.searchInput;
      await this.$store.dispatch('viewState/updateViewState', {
        name: ViewStateType.COMMUNICATION_GROUP_SEARCH,
        value: currentSearchInput,
      });
      this.loadingInitialData = true;
      const response = await HTTP.post('/api/communication-campaigns/contact-groups/' + this.recipientTypeId, {
        filtersSelected: this.filtersSelected,
        searchInput: currentSearchInput,
      });
      this.contactGroups = response.data.data;
    } catch (ex) {
      this.$notify({
        group: 'main',
        type: 'error',
        title: 'Error Getting Contact Groups',
        text: 'Something went wrong getting contact groups!',
      });
    } finally {
      this.loadingInitialData = false;
    }
  }

  async actionSelected(actionId: number) {
    this.closeActions();
    switch (actionId) {
      case ContactGroupAction.RenameGroup:
        this.renameGroup();
        break;
      case ContactGroupAction.CopyGroup:
        this.copyGroup();
        break;
      case ContactGroupAction.DeleteGroup:
        this.deleteGroup();
        break;
    }
  }

  async groupClicked(contactGroup: ContactGroupModel) {
    this.selectedGroup = contactGroup;
    try {
      this.loadingInitialData = true;
      const contactIds = await HTTP.post('/api/communication-campaigns/contacts/', {
        contactTypeId: this.recipientTypeId,
        contactGroupId: this.selectedGroup.id
      });
      this.loadingInitialData = false;
      this.$emit('groupSelected', contactIds.data.data, this.selectedGroup);
    } catch (ex) {
      this.$notify({
        group: 'main',
        type: 'error',
        title: 'Error Updating Group',
        text: 'Something went wrong updating group!',
      });
    }
  }

  deleteGroup() {
    this.showDeleteGroupConfirmationModal = true;
  }

  async deleteGroupConfirmed(){
    this.showDeleteGroupConfirmationModal = false;
    try {
      this.loadingInitialData = true;
      await HTTP.post('/api/communication-campaigns/remove-contact-group/'+ this.expandingContactId, {
        contactTypeId: this.recipientTypeId
      });
    } catch (ex) {
      this.$notify({
        group: 'main',
        type: 'error',
        title: 'Error Updating Group',
        text: 'Something went wrong updating group!',
      });
    } finally {
      this.loadingInitialData = false;
    }
    await this.getAllContactGroups();
  }

  openActions(contactGroupId: number) {
    this.showActions = !this.showActions;
    this.expandingContactId = contactGroupId;
  }

  closeActions() {
    this.showActions = false;
  }

  renameGroup() {
    this.showRenameGroupModal = true;
    this.newGroupName = this.selectedGroup.groupName;
  }

  async renameGroupConfirmed(){
    this.showRenameGroupModal = false;
    try {
      this.loadingInitialData = true;
      await HTTP.put('/api/communication-campaigns/rename-contact-group/' + this.expandingContactId, {
        contactTypeId: this.recipientTypeId,
        contactGroupName: this.newGroupName,
      });
    } catch (ex) {
      this.$notify({
        group: 'main',
        type: 'error',
        title: 'Error Updating Group',
        text: 'Something went wrong updating group!',
      });
    } finally {
      this.loadingInitialData = false;
    }
    this.newGroupName = '';
    await this.getAllContactGroups();
  }

  copyGroup() {
    this.showCopyGroupModal = true;
    this.newGroupName = this.selectedGroup.groupName;
  }

  async setDefaultLoggedInUser() {
    const appUserId = this.$store.state.user.appUserId;
    let loggedUser = this.$store.getters['user/getUser'](appUserId);
    if (!loggedUser) {
      await this.$store.dispatch('user/loadUsers');
      loggedUser = this.$store.getters['user/getUser'](appUserId);
    }

    this.defaultLoggedInUser = {
      appUserId: (this as any).$store.state.user.appUserId,
      filterTypeId: communicationsFilterEnums.TEAM_MEMBER_FILTER_TYPE,
      name: `${loggedUser.firstName} ${loggedUser.lastName}`
    }
  }

  setDefaultFilters() {
    this.defaultFilters.push(this.defaultLoggedInUser);
  }

  async copyGroupConfirmed(){
    this.showCopyGroupModal = false;
    try {
      this.loadingInitialData = true;
      await HTTP.post('/api/communication-campaigns/duplicate-contact-group/' + this.expandingContactId, {
        contactTypeId: this.recipientTypeId,
        contactGroupName: this.newGroupName,
      });
    } catch (ex) {
      this.$notify({
        group: 'main',
        type: 'error',
        title: 'Error Updating Group',
        text: 'Something went wrong updating group!',
      });
    } finally {
      this.loadingInitialData = false;
    }
    this.newGroupName = '';
    await this.getAllContactGroups();
  }
}



// WEBPACK FOOTER //
// ./src/components/communications/campaign/CampaignGroupCard/CampaignGroupCard.ts